import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { styled } from 'styled-components'

const StyledSliderWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`

const StyledRangeInput = styled.input.attrs({ type: 'range' })<{
  value: number
  min: number
  max: number
  $touched: boolean
}>`
  -webkit-appearance: none;
  appearance: none;
  background: var(--colour-surface-white);
  border-radius: 100px;
  height: 12px;
  margin: 0;
  outline: none;
  position: relative;
  width: 100%;
  z-index: 1;

  &::-webkit-slider-runnable-track {
    background: var(--colour-surface-white);
    border-radius: 100px;
    cursor: pointer;
    height: 12px;
    width: 100%;
  }

  &::-moz-range-track {
    background: var(--colour-surface-white);
    border-radius: 100px;
    cursor: pointer;
    height: 12px;
    width: 100%;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    background: var(--colour-surface-aubergine-bold);
    border-radius: 50%;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    visibility: ${({ $touched }) => ($touched ? 'visible' : 'hidden')};
    height: 24px;
    position: relative;
    top: -6px;
    transition: transform 0.3s ease;
    width: 24px;
    z-index: 5;
  }

  &::-moz-range-thumb {
    background: var(--colour-surface-aubergine-bold);
    border-radius: 50%;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    height: 24px;
    position: relative;
    transition: transform 0.3s ease;
    width: 24px;
    z-index: 5;
  }

  &:hover::-webkit-slider-thumb {
    transform: scale(1.1);
  }

  &:hover::-moz-range-thumb {
    transform: scale(1.1);
  }
`

const StyledDotsBackground = styled.div<{ value: number; min: number; max: number; $touched: boolean }>`
  background: ${({ value, min, max, $touched }) => {
    if (!$touched) return 'var(--colour-surface-white);'
    const percentage = ((value - min) / (max - min)) * 100
    return `linear-gradient(to right, var(--colour-surface-aubergine-bold) ${percentage}%, transparent ${percentage}%)`
  }};
  border-radius: 100px;
  display: flex;
  left: 0;
  height: 12px;
  justify-content: space-between;
  padding: 0 6px;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  z-index: 2;
`

const StyledDot = styled.div`
  background-color: var(--colour-text-secondary-inverse);
  border-radius: 50%;
  height: 4px;
  margin: 4px 4px 0 4px;
  width: 4px;
  z-index: 2;
  &:hover {
    transform: scale(1.1);
  }
`

const StyledUntouchedRangeDot = styled.div`
  border: 10px solid var(--colour-surface-aubergine-bold);
  border-radius: 50%;
  background: var(--colour-text-secondary-inverse);
  height: 24px;
  left: calc(50% - 12px);
  position: absolute;
  width: 24px;
  z-index: 5;
  &:hover {
    transform: scale(1.1);
  }
`

export interface DSRangeSliderProps {
  min?: number
  max?: number
  step?: number
  name?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
}

export function DSRangeSlider({ min = 0, max = 5, step = 1, name, onChange }: DSRangeSliderProps) {
  const [value, setValue] = useState(min)
  const [touched, setTouched] = useState(false)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(e)
    setValue(Number(e.target.value))
    setTouched(true)
  }

  return (
    <StyledSliderWrapper>
      <StyledDotsBackground $touched={touched} max={max} min={min} value={value}>
        {[...Array(max - min + 1)].map((_, index) => (
          <StyledDot key={index} />
        ))}
      </StyledDotsBackground>
      {!touched && <StyledUntouchedRangeDot />}
      <StyledRangeInput
        $touched={touched}
        max={max}
        min={min}
        name={name}
        onChange={handleChange}
        step={step}
        value={value}
      />
    </StyledSliderWrapper>
  )
}
